/**
 * Web Push subscription helper for Bula Golf.
 *
 * Coordinates with `/sw.js` (which actually shows notifications) and the
 * backend (`/api/push/*`) to manage the user's browser-push subscription.
 *
 * Design notes:
 * - We never auto-prompt for notification permission. Users must opt in via
 *   a deliberate click in Profile → "Tournament push alerts".
 * - In dev (CRA dev server) the SW isn't registered, so subscribe() returns
 *   `not_supported`. Same for browsers without PushManager (Safari < 16.4
 *   without PWA installation).
 */
import { api } from "@/lib/api";

function urlBase64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function pushPermissionState() {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission; // "default" | "granted" | "denied"
}

export async function getActiveSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function subscribeToPush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  // Permission must be requested via a user gesture; this function expects
  // to be called from a click handler.
  let perm = Notification.permission;
  if (perm === "default") {
    perm = await Notification.requestPermission();
  }
  if (perm !== "granted") return { ok: false, reason: "permission_denied" };

  const { data: keyResp } = await api.get("/push/vapid-public-key");
  const vapidKey = urlBase64ToUint8Array(keyResp.key);

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKey,
    });
  }
  const j = sub.toJSON();
  await api.post("/push/subscribe", {
    endpoint: j.endpoint,
    keys: { p256dh: j.keys.p256dh, auth: j.keys.auth },
    expirationTime: j.expirationTime ?? null,
    userAgent: navigator.userAgent,
  });
  return { ok: true };
}

export async function unsubscribeFromPush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true, alreadyOff: true };
  try {
    await api.request({
      url: "/push/subscribe",
      method: "delete",
      data: { endpoint: sub.endpoint },
    });
  } catch {}
  await sub.unsubscribe();
  return { ok: true };
}

export async function sendTestPush() {
  try {
    const { data } = await api.post("/push/test");
    return { ok: true, ...data };
  } catch (e) {
    return { ok: false, error: e.response?.data?.detail || e.message };
  }
}
